import { processHeavyImage, extractEmbeddedJpeg } from './image-processing';

// Lazy-load browser-image-compression (~50 kB) only when actually needed — same
// pattern as heic-to below — so it stays out of the eager property-form chunk.
const loadImageCompression = () => import('browser-image-compression').then(m => m.default);
import type { ImageWorkerRequest, ImageWorkerResponse } from './image-worker';

const HEIC_EXT_RE = /\.(heic|heif)$/i;

// RAW / TIFF containers a browser <canvas> can't decode. iPhone ProRAW shoots
// .DNG (a 45 MB+ TIFF holding a full-res JPEG preview); other cameras shoot
// vendor RAW. We can't demosaic raw sensor data in the browser, but every one
// of these embeds a full-resolution JPEG preview we CAN pull out.
const RAW_EXT_RE = /\.(dng|tiff?|arw|cr2|cr3|nef|nrw|raf|rw2|orf|srw|pef|dcr)$/i;

function isRawOrTiff(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/x-adobe-dng' || type === 'image/dng'
    || type === 'image/tiff' || type === 'image/x-tiff'
    || type.startsWith('image/x-') /* vendor RAW MIME variants */) {
    return true;
  }
  // RAW files very often arrive with an empty or octet-stream MIME — fall back
  // to the extension, same as the HEIC path.
  if (type === '' || type === 'application/octet-stream') {
    return RAW_EXT_RE.test(file.name);
  }
  return false;
}

export const HEIC_UNREADABLE_MESSAGE =
  'No se pudo leer esta foto HEIC. En el iPhone abre Ajustes → Cámara → Formatos y elige '
  + '"Más compatible" para que las fotos se guarden como JPEG, o envíatelas por WhatsApp o '
  + 'correo y sube las copias.';

/**
 * Decode a HEIC file to JPEG, in the order most likely to succeed.
 *
 * 1. heic-to (libheif 1.22.2 / libde265 1.0.16). It replaced heic2any, which
 *    npm pinned at 0.0.4 — published 2023-03-29 and never updated. That build's
 *    decoder rejects HEIC from recent iPhones outright with "ERR_LIBHEIF format
 *    not supported", which is not an edge case: GlitchTip #714 was 8 upload
 *    batches, ~96 photos, and SEVEN of the eight uploaded nothing at all. The
 *    give-away was one mixed batch that went 6 of 17 — a camera roll spanning
 *    two iOS eras decodes partially, which a wholly-broken HEIC path never would.
 * 2. The browser's own decoder. It is a different implementation from the WASM
 *    one and Safari decodes HEIC natively via the OS, so it can still succeed
 *    where libheif gives up. Chrome desktop generally cannot — this rescues
 *    some failures, not all of them.
 *
 *    This step also absorbs the case that used to need its own branch: a JPEG
 *    or PNG wearing a .heic name (WhatsApp exports, renamed files), which once
 *    failed an entire 11-photo batch (GlitchTip #719). heic2any announced it
 *    with "ERR_USER … already browser readable" and we string-matched that;
 *    heic-to emits no ERR_* codes at all, so the check would now be dead code.
 *    A real JPEG simply decodes here instead — and comes out correctly named.
 *
 * Only if both fail do we give up, and then with an instruction the agent can
 * act on rather than "intenta de nuevo", which for a deterministic decode
 * failure was advice that could never work.
 */
// Exported for the decode-ladder test; callers should go through optimizeImage.
export async function heicToJpeg(file: File): Promise<File> {
  const asJpegFile = (blob: Blob) =>
    new File([blob], file.name.replace(HEIC_EXT_RE, '.jpg') || 'image.jpg', { type: 'image/jpeg' });

  try {
    const { heicTo } = await import('heic-to');
    return asJpegFile(await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 }));
  } catch {
    const native = await nativeDecodeToJpeg(file).catch(() => null);
    if (native) return asJpegFile(native);

    // ponytail: still reported, NOT expectedError. A failure that survives both
    // a current libheif and the OS decoder is a genuine unknown worth seeing.
    // Revisit once #714 is confirmed dead.
    throw new Error(HEIC_UNREADABLE_MESSAGE);
  }
}

/** Last-resort decode through the browser/OS image decoder. Null if it can't. */
async function nativeDecodeToJpeg(file: File): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92));
  } finally {
    bitmap.close?.();
  }
}

// iPhones shoot HEIC. Browsers report it inconsistently: sometimes
// `image/heic`/`image/heif`, sometimes a `-sequence` variant (Live Photos),
// and often an EMPTY MIME type — in which case only the extension tells us.
function isHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif'
    || type === 'image/heic-sequence' || type === 'image/heif-sequence') {
    return true;
  }
  if (type === '' || type === 'application/octet-stream') {
    return HEIC_EXT_RE.test(file.name);
  }
  return false;
}

// Ordinary web-image extensions. Samsung Internet — and several Android
// gallery/file providers — hand files to <input type=file> with an EMPTY MIME
// type, so a normal 20240607_143022.jpg fails `type.startsWith('image/')` and
// was silently dropped by the filter below. That's the reported "Samsung photo
// won't upload / nothing happens" bug.
const WEB_IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|avif)$/i;

// File-input filter for image pickers. Plain `file.type.startsWith('image/')`
// silently drops JPEG/HEIC files that arrive with an empty (or wrong) MIME — the
// user picks a photo and nothing happens. Accept anything the pipeline can
// decode, falling back to the extension when the MIME is missing.
export function isAcceptableImage(file: File): boolean {
  return file.type.startsWith('image/')
    || isHeic(file)
    || isRawOrTiff(file)
    || WEB_IMAGE_EXT_RE.test(file.name);
}

// Above this size we route through the low-memory createImageBitmap path
// instead of browser-image-compression. The latter decodes the whole image
// into a full-resolution canvas bitmap before downscaling — a 48 MP photo is
// a ~190 MB bitmap, and three in flight will crash iOS Safari. createImageBitmap
// with resize options lets the JPEG decoder downsample *during* decode, so the
// full-res bitmap never exists.
const LARGE_IMAGE_BYTES = 6 * 1024 * 1024;

// Safari ≤14 and other old engines lack createImageBitmap entirely, so the
// low-memory downscale path throws a ReferenceError ("Can't find variable:
// createImageBitmap"). Detect it once and route those browsers to the
// iterative compressor (which decodes via <img>/<canvas>, no bitmap needed).
const SUPPORTS_CREATE_IMAGE_BITMAP = typeof createImageBitmap !== 'undefined';

// True for inputs that need the low-memory path or special handling, so callers
// (e.g. the gallery uploader) can throttle concurrency for heavy batches.
export function isHeavyImage(file: File): boolean {
  return isRawOrTiff(file) || isHeic(file) || file.size > LARGE_IMAGE_BYTES;
}

// ── Image Web Worker ───────────────────────────────────────────────────────
// One lazily-created worker handles all heavy processing so the 45 MB read +
// IFD parse never blocks the UI thread. Requests are correlated by id; a worker
// crash rejects all in-flight requests and drops the instance so the next call
// transparently falls back to main-thread processing.
let imageWorker: Worker | null = null;
let workerUnavailable = false;
let nextRequestId = 0;
const pendingRequests = new Map<number, { resolve: (b: Blob) => void; reject: (e: Error) => void }>();

function getImageWorker(): Worker | null {
  if (workerUnavailable || typeof Worker === 'undefined') return null;
  if (imageWorker) return imageWorker;
  try {
    const worker = new Worker(new URL('./image-worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<ImageWorkerResponse>) => {
      const data = e.data;
      const pending = pendingRequests.get(data.id);
      if (!pending) return;
      pendingRequests.delete(data.id);
      if (data.ok && data.blob) {
        pending.resolve(data.blob);
        return;
      }
      pending.reject(new Error(data.error || 'image worker failed'));
    };
    worker.onerror = (e) => {
      const err = new Error(e.message || 'image worker crashed');
      for (const { reject } of pendingRequests.values()) reject(err);
      pendingRequests.clear();
      worker.terminate();
      imageWorker = null;
      workerUnavailable = true; // stop retrying a broken worker; use main thread
    };
    imageWorker = worker;
    return worker;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

function processInWorker(
  blob: Blob,
  opts: { isRaw: boolean; maxDimension: number; quality: number; mimeType: string },
): Promise<Blob> {
  const worker = getImageWorker();
  if (!worker) return Promise.reject(new Error('image worker unavailable'));
  return new Promise<Blob>((resolve, reject) => {
    const id = ++nextRequestId;
    pendingRequests.set(id, { resolve, reject });
    const req: ImageWorkerRequest = { id, blob, ...opts };
    worker.postMessage(req);
  });
}

interface OptimizeOptions {
  maxDimension?: number;
  maxSizeMB?: number;
  quality?: number;
  /** Output format. Omit to keep the source format (HEIC still becomes JPEG). */
  fileType?: 'image/webp' | 'image/jpeg' | 'image/png';
}

/**
 * Decode + resize + re-encode an image for upload.
 *
 * - HEIC (iPhone default): converted to JPEG first via heic-to (lazy-imported
 *   WASM), since most browsers can't decode HEIC through a <canvas>.
 * - RAW/DNG (iPhone ProRAW, camera RAW): the embedded full-res JPEG preview is
 *   extracted; the 45 MB original is never uploaded.
 * - Heavy inputs (RAW previews, big DSLR JPEGs) run through a low-memory
 *   createImageBitmap downscale, preferentially inside a Web Worker so the UI
 *   thread stays responsive. Falls back to main-thread processing, then to the
 *   battle-tested iterative compressor.
 * - Small images keep using browser-image-compression, whose iterative quality
 *   search reliably hits the maxSizeMB target.
 */
// createImageBitmap-free RAW path for old engines: pull the embedded full-res
// JPEG preview out of the container (pure DataView work, no bitmap), then resize
// it with the iterative compressor. Throws the same Spanish message as the
// worker path when the RAW has no usable embedded preview.
async function compressRawPreview(
  file: File,
  opts: { maxDimension: number; maxSizeMB: number; quality: number; fileType?: OptimizeOptions['fileType'] },
): Promise<Blob> {
  const preview = await extractEmbeddedJpeg(file);
  if (!preview) {
    throw new Error('No se pudo procesar esta imagen RAW. Exporta la foto a JPEG e inténtalo de nuevo.');
  }
  const previewFile = new File([preview], file.name.replace(RAW_EXT_RE, '.jpg') || 'image.jpg', {
    type: 'image/jpeg',
  });
  const imageCompression = await loadImageCompression();
  return imageCompression(previewFile, {
    maxWidthOrHeight: opts.maxDimension,
    maxSizeMB: opts.maxSizeMB,
    initialQuality: opts.quality,
    useWebWorker: true,
    ...(opts.fileType ? { fileType: opts.fileType } : {}),
  });
}

// Safari's canvas can't encode WebP and does NOT throw — it silently hands back
// a lossless PNG (2-3 MB per gallery photo, ~20% of uploads until 2026-09-17).
// Detect once and ask for JPEG instead.
let webpEncodable: boolean | undefined;
function canEncodeWebp(): boolean {
  if (webpEncodable === undefined) {
    try {
      webpEncodable = document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp');
    } catch {
      webpEncodable = false;
    }
  }
  return webpEncodable;
}

export async function optimizeImage(file: File, opts: OptimizeOptions = {}): Promise<Blob> {
  const { maxDimension = 2600, maxSizeMB = 3, quality = 0.85 } = opts;
  const fileType = opts.fileType === 'image/webp' && !canEncodeWebp() ? 'image/jpeg' : opts.fileType;

  const raw = isRawOrTiff(file);

  // RAW/DNG: hand the original straight to the worker, which extracts the
  // embedded JPEG and downscales it. Worker failure (unsupported/crash) retries
  // on the main thread, which still surfaces the helpful "export to JPEG" error
  // if there's genuinely no embedded preview.
  if (raw) {
    const mimeType = fileType || 'image/jpeg';
    // Old engines without createImageBitmap can't run the low-memory downscale.
    // Pull out the embedded preview and let the iterative compressor resize it.
    if (!SUPPORTS_CREATE_IMAGE_BITMAP) {
      return compressRawPreview(file, { maxDimension, maxSizeMB, quality, fileType });
    }
    try {
      return await processInWorker(file, { isRaw: true, maxDimension, quality, mimeType });
    } catch (err) {
      if ((err as Error).message?.startsWith('No se pudo procesar')) throw err; // real "no preview"
      return processHeavyImage(file, { isRaw: true, maxDimension, quality, mimeType });
    }
  }

  // HEIC → JPEG before any canvas work.
  let input: File = file;
  if (isHeic(file)) {
    input = await heicToJpeg(file);
  }

  // Heavy non-RAW inputs (big JPEGs, converted HEIC): worker → main thread →
  // iterative compressor. Old engines without createImageBitmap skip the heavy
  // path entirely and fall straight through to the iterative compressor below.
  if (input.size > LARGE_IMAGE_BYTES && SUPPORTS_CREATE_IMAGE_BITMAP) {
    const mimeType = fileType || (input.type.startsWith('image/') ? input.type : 'image/jpeg');
    try {
      return await processInWorker(input, { isRaw: false, maxDimension, quality, mimeType });
    } catch {
      try {
        return await processHeavyImage(input, { isRaw: false, maxDimension, quality, mimeType });
      } catch {
        // createImageBitmap / OffscreenCanvas unsupported — fall through to the
        // battle-tested compressor rather than failing the upload.
      }
    }
  }

  const imageCompression = await loadImageCompression();
  return imageCompression(input, {
    maxWidthOrHeight: maxDimension,
    maxSizeMB,
    initialQuality: quality,
    useWebWorker: true,
    ...(fileType ? { fileType } : {}),
  });
}
